"use client";

import { type RefObject, useEffect, useState } from "react";

/** Keep the entire drawer inside the visible viewport when a keyboard opens. */
export function useDrawerViewport(
  open: boolean,
  elementRef?: RefObject<HTMLDivElement | null>,
) {
  const [viewport, setViewport] = useState<{
    height: number;
    bottom: number;
  } | null>(null);
  useEffect(() => {
    if (!open) return;
    const visual = window.visualViewport;
    const update = () => {
      const height = visual?.height ?? window.innerHeight;
      const bottom = Math.max(
        0,
        window.innerHeight - height - (visual?.offsetTop ?? 0),
      );
      if (elementRef?.current) {
        // Keyboard animation emits resize/scroll every frame. Updating the
        // drawer directly avoids rerendering the entire comment thread.
        const element = elementRef.current;
        element.style.height = `calc(${height}px - env(safe-area-inset-top) - 8px)`;
        element.style.maxHeight = element.style.height;
        element.style.bottom = `${bottom}px`;
      } else {
        setViewport((previous) =>
          previous?.height === height && previous.bottom === bottom
            ? previous
            : { height, bottom },
        );
      }
    };
    update();
    window.addEventListener("resize", update);
    visual?.addEventListener("resize", update);
    visual?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      visual?.removeEventListener("resize", update);
      visual?.removeEventListener("scroll", update);
    };
  }, [open, elementRef]);
  return open && viewport
    ? {
        height: `calc(${viewport.height}px - env(safe-area-inset-top) - 8px)`,
        bottom: viewport.bottom,
      }
    : undefined;
}
